import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import { isBadNote, noteText } from './notes'
import { Chosen, CreditsOffer, DiscardsOffer, LayoutOffer, StoryboardOffer } from './Offers.jsx'
import { SendIcon } from './Icons.jsx'
import PostModal from './PostModal.jsx'
import { QuestionOptions, QuickActions, Starters } from './Starters.jsx'

/** The two turns nobody types. Sent once each, and the server remembers that they were. */
const OPENING = 'opening'
const SITE_READ = 'site-read'

/**
 * How a campaign ended, said in the owner's language.
 *
 * The jobs host reports numbers rather than a sentence — it cannot know which language this
 * browser is in — so the wording lives here. When nothing matched, the shop's own categories come
 * with it: "there are no gramophones in your catalog" is only half an answer without "here is what
 * there is".
 */
/**
 * The posts a turn produced, in the conversation, as things to tap.
 *
 * Shared by the campaign's report and by any single reply that touched a post, because the note
 * above them promises posts are here and the promise has to be the same promise either way.
 */
/** Cards from a stored turn written before the payload was camelCase. */
function normalisePost(post) {
  return {
    id: post.id ?? post.Id,
    image: post.image ?? post.Image,
    name: post.name ?? post.Name,
    plannedFor: post.plannedFor ?? post.PlannedFor,
    before: post.before ?? post.Before ?? null,
  }
}

function PostCards({ posts, onOpenPost, onUndo }) {
  const { t, i18n } = useTranslation()

  return (
    <div className="chatposts">
      {posts.map(normalisePost).map((post) => (
        <div key={post.id} className="chatpost__slot">
        <button type="button" className="chatpost" onClick={() => onOpenPost(post.id)}>
          {/* A designer showing work says "this is what it was, this is what it is now". The pair
              is drawn only when the turn actually replaced the picture; a change to the words
              leaves one image, as it always did. */}
          {post.before ? (
            <span className="chatpost__pair">
              <img className="chatpost__image chatpost__image--was" src={post.before} alt="" loading="lazy" />
              <img className="chatpost__image" src={post.image} alt="" loading="lazy" />
            </span>
          ) : post.image ? (
            <img className="chatpost__image" src={post.image} alt="" loading="lazy" />
          ) : (
            <span className="chatpost__image chatpost__image--empty">🖼</span>
          )}
          <span className="chatpost__name">{post.name ?? `#${post.id}`}</span>
          {post.plannedFor && (
            <span className="chatpost__when">
              {new Date(post.plannedFor).toLocaleDateString(i18n.resolvedLanguage, {
                day: 'numeric',
                month: 'short',
              })}
            </span>
          )}
        </button>

        {/* Taking it back is a tap, not a sentence to compose. Only offered where the turn
            changed something that can be taken back. */}
        {onUndo && post.before && (
          <button type="button" className="chatpost__undo" onClick={() => onUndo(post.id)}>
            {t('posts.undo')}
          </button>
        )}
        </div>
      ))}
    </div>
  )
}

/**
 * What a redraw did. Short on purpose: nothing was written, nothing was charged, and the only
 * thing worth saying is which posts changed and what they carry now.
 */
/**
 * What the workshop did, when it ran where nobody was watching.
 *
 * The job writes numbers because the language the owner reads is not something a background job can
 * know. Its own sentence — when the model gave one, which IS in their language — leads; the count
 * follows it, and says honestly how many of the posts it was given it never reached.
 */
function DesignReport({ design, reply }) {
  const { t } = useTranslation()
  const left = Math.max(0, (design.asked ?? 0) - (design.changed ?? 0))

  return (
    <>
      {reply || t('campaign.designDone', { count: design.changed ?? 0 })}
      {left > 0 && (
        <div className="bubble__note">{t('campaign.designLeft', { count: left })}</div>
      )}
    </>
  )
}

function RecastReport({ recast, onOpenPost }) {
  const { t } = useTranslation()

  const what = [
    recast.design && t('campaign.recastDesign'),
    recast.redrawn && t('campaign.recastRedrawn'),
    recast.video === true && t('campaign.recastVideo'),
    recast.story === true && t('campaign.recastStory'),
    recast.headline && t('campaign.recastHeadline', { text: recast.headline }),
    recast.template && t('campaign.recastLook', { look: t(`profile.looks.${recast.template}`) }),
  ].filter(Boolean)

  return (
    <>
      {t('campaign.recastDone', { count: recast.changed })}
      {what.length > 0 && <div className="bubble__note">{what.join(' · ')}</div>}
      {recast.skipped > 0 && (
        <div className="bubble__note">{t('campaign.recastSkipped', { count: recast.skipped })}</div>
      )}
      {recast.posts?.length > 0 && <PostCards posts={recast.posts} onOpenPost={onOpenPost} />}

      {/* Drawn again and identical. Said plainly, because a lever can be set, stored and honoured
          and still change nothing anybody can see — asking for dark type on a card that is already
          drawn in it, say — and "готово" over an unchanged picture is the platform reporting the
          request instead of the result. */}
      {recast.same > 0 && (
        <>
          <div className="bubble__note">{t('campaign.recastSame', { count: recast.same })}</div>
          {recast.samePosts?.length > 0 && <PostCards posts={recast.samePosts} onOpenPost={onOpenPost} />}
        </>
      )}

      {/* Asked for a new picture and did not get one. Which kind of no it was decides what to do
          next, so it is said rather than left to be noticed on the third attempt. */}
      {recast.notDrawn > 0 && (
        <div className="bubble__note bubble__note--bad">
          {t(`campaign.notDrawn.${recast.notDrawnWhy ?? 'failed'}`, {
            count: recast.notDrawn,
            defaultValue: t('campaign.notDrawn.failed', { count: recast.notDrawn }),
          })}
        </div>
      )}

      {/* Asked for a video and got a still, and told so — with the posts it happened to, because
          the way forward is to open one and give it more photographs. This line is the difference
          between a platform that could not and one that said it did. */}
      {recast.withoutVideo > 0 && (
        <>
          <div className="bubble__note bubble__note--bad">
            {t('campaign.recastNoVideo', { count: recast.withoutVideo })}
          </div>
          {recast.withoutVideoPosts?.length > 0 && (
            <PostCards posts={recast.withoutVideoPosts} onOpenPost={onOpenPost} />
          )}
        </>
      )}

      <div className="bubble__note">{t('campaign.recastCost')}</div>
    </>
  )
}

/**
 * What drawing a story came to. The pictures are the post's now, and the price is said again
 * beside the result — what publishing will take — so the owner is never charged a number they
 * last saw three messages ago.
 */
function StoryboardReport({ story, onOpenPost }) {
  const { t } = useTranslation()

  if (story.failed) {
    return <div className="bubble__note bubble__note--bad">{t(`campaign.storyboardFailed.${story.failed}`, { defaultValue: t('campaign.storyboardFailed.storyboard-failed') })}</div>
  }

  return (
    <>
      {t(story.video ? 'campaign.storyboardDone' : 'campaign.storyboardDoneNoVideo', { count: story.scenes })}
      {story.posts?.length > 0 && <PostCards posts={story.posts} onOpenPost={onOpenPost} />}
      <div className="bubble__note">
        {t('campaign.storyboardCost', { credits: story.credits, scenes: story.sceneCredits })}
      </div>
    </>
  )
}

function CampaignReport({ report, onOpenPost, onShowCalendar, onShowProfile }) {
  const { t } = useTranslation()

  if (report.written > 0) {
    return (
      <>
        {/* "and placed on the calendar" said about posts nobody placed is the same fault as
            placing them: a schedule the owner never chose, reported to them as though they had. */}
        {t(report.placed > 0 ? 'campaign.done' : 'campaign.doneUnplaced', { count: report.written })}

        {/* The posts themselves, right here. Tapping one opens the same preview the calendar
            uses — the conversation is where the owner already is, and sending them to another
            tab to look at finished work was one trip more than the work deserved. */}
        {/* The one thing left to do. Drafts sit on the calendar until somebody approves them —
            that is the whole safety of this product — so the sentence after "done" is what turns
            them into something that goes out. A business with no page connected is told that
            instead: promising automatic publishing to nowhere would be a lie. */}
        <div className="bubble__note">
          {report.connected > 0 ? t('campaign.approveNext') : t('campaign.connectFirst')}{' '}
          <button
            type="button"
            className="link"
            onClick={report.connected > 0 ? onShowCalendar : onShowProfile}
          >
            {t(report.connected > 0 ? 'campaign.openCalendar' : 'campaign.openProfile')}
          </button>
        </div>

        {report.posts?.length > 0 && <PostCards posts={report.posts} onOpenPost={onOpenPost} />}

        {report.withoutVideo > 0 && (
          <div className="bubble__note bubble__note--bad">
            {t('campaign.noVideo', { count: report.withoutVideo })}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {t('campaign.nothingMatched', { asked: report.asked })}
      {report.categories?.length > 0 && (
        <div className="bubble__note">{t('campaign.tryThese', { list: report.categories.join(', ') })}</div>
      )}
    </>
  )
}

/** How long an unfinished wait is still worth resuming. Past this the job is not coming back. */
const WAIT_WINDOW_MS = 20 * 60 * 1000

/** The turns a background job writes when it is done — each one ends a wait. */
const DONE_MARKERS = ['campaign-done', 'recast-done', 'storyboard-done', 'design-done']

/**
 * Whether the stored conversation is still waiting on a campaign.
 *
 * The wait used to live only in this component's state, so switching to the calendar and back
 * ended it: the loader vanished and the report never arrived, however well the jobs host had
 * done its work. The conversation itself is the record — an assistant turn that says "working"
 * with no report after it means the answer is still coming.
 */
function stillWaiting(turns) {
  const lastWorking = turns.findLastIndex((turn) => turn.working)

  if (lastWorking === -1) return false
  if (turns.slice(lastWorking + 1).some((turn) => DONE_MARKERS.includes(turn.marker))) return false

  const started = turns[lastWorking].createdAt

  return started ? Date.now() - new Date(started).getTime() < WAIT_WINDOW_MS : false
}

/**
 * A stored turn, as the portal draws it.
 *
 * Nothing here may throw. The whole transcript is mapped in one go, so one row the portal cannot
 * read used to take every other turn down with it and leave an empty conversation — the owner's
 * history, gone, because of one payload.
 */
function fromStored(turn) {
  let payload = {}

  try {
    if (turn?.payload) payload = JSON.parse(turn.payload) ?? {}
  } catch {
    // A turn whose particulars cannot be read still said something; the text is what matters.
  }

  if (!turn) return { from: 'assistant', text: '', questions: [], posts: [], notes: [] }

  return {
    from: turn.fromOwner ? 'owner' : 'assistant',
    text: turn.text,
    marker: turn.marker,
    createdAt: turn.createdAt,
    questions: payload.questions ?? [],
    written: payload.postIds?.length ?? 0,
    action: payload.action,
    posts: payload.posts ?? [],
    working: payload.working ?? false,
    truncated: payload.catalogTruncated ?? false,
    notes: payload.notes ?? [],

    // How a campaign ended. Written by the jobs host as numbers; the sentence is this side's job,
    // because the language the owner reads is not something a background job can know.
    campaign: payload.campaign ?? null,

    // Posts that already existed, drawn again with a video, a story, a different look or a new
    // picture. Nothing was written, so it is not a campaign and does not read like one.
    recast: payload.recast ?? null,

    // What the workshop did in the background, as numbers. Only written when it ran there.
    design: payload.design ?? null,

    // A price the platform is asking the owner to agree to, named rather than worded.
    offer: payload.offer ?? null,

    // With the "storyboard" offer: how many pictures, at what price, as buttons.
    choices: payload.choices ?? null,

    // With the "layout" offer: the shop's own product drawn in each layout, and the turn a tap
    // names so the server can find the campaign it wrote down.
    layouts: payload.layouts ?? null,
    turnId: turn.id,

    // A story drawn for a post, as the jobs host reported it — and only that. The turn that says
    // the drawing has started carries no report, and must not be drawn as one.
    storyboard: turn.marker === 'storyboard-done' ? (payload.storyboard ?? null) : null,
  }
}

/**
 * The assistant.
 *
 * Everything the buttons do, asked for in a sentence — and asked back about when what was said is
 * not enough. Its questions arrive with answers attached, because tapping "Да" is work and typing
 * it is more work.
 */
export default function Campaign({
  onPostsWritten,
  onShowCalendar,
  onShowProfile,
  siteRead,
  business,
  timezoneId,
  canManage,
  focus,
  onFocus,
}) {
  const { t, i18n } = useTranslation()
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [working, setWorking] = useState(false)

  // Nothing is drawn and nothing is said until the stored conversation is back: the chat that
  // opened on an empty screen and introduced itself again is exactly the bug this fixes.
  const [loaded, setLoaded] = useState(false)

  // The post opened from a report card, as the preview knows it. Fetched fresh on every open:
  // the report was written minutes or days ago, and the post has been living its life since.
  const [openPost, setOpenPost] = useState(null)

  // Undo from under a card, without composing a sentence for it.
  const [undoing, setUndoing] = useState(null)
  const endRef = useRef(null)
  const textRef = useRef(null)

  // The newest report this browser has already drawn. Anything newer ends the wait — comparing
  // ids rather than "is it the last turn" survives the owner typing while the job runs.
  const reportSeen = useRef(0)

  // Held in a ref rather than read from the closure: the poll below must not be rebuilt when the
  // parent re-renders, and calling this is what makes the parent re-render.
  const wrote = useRef(onPostsWritten)
  wrote.current = onPostsWritten

  // What the owner last asked for, kept so that agreeing to a price can carry on with it rather
  // than making them type the same sentence again.
  const lastAsk = useRef(null)

  // Where the business stands, for the starting points offered under the transcript. Read when
  // the chat opens and again after every turn: a campaign that just wrote five drafts changes
  // what is worth offering next.
  const [starters, setStarters] = useState(null)

  // "Друго…" was tapped: the cursor is in the box and the box says so, until something is typed.
  const [otherAsked, setOtherAsked] = useState(false)

  // Each read is numbered and an answer older than the newest read is dropped: the one fired
  // after a turn and the one fired when the report lands can cross in the post, and the stale
  // one used to win.
  const startersAsked = useRef(0)

  const loadStarters = useCallback(() => {
    const asked = ++startersAsked.current

    api
      .assistantState()
      .then((state) => {
        if (asked === startersAsked.current) setStarters(state)
      })
      .catch(() => {
        // No starting points is a blank box, which is what the chat always was.
      })
  }, [])

  const acceptOffer = useCallback(async () => {
    setBusy(true)

    try {
      await api.buyDiscards()

      // The credit is gone from the ledger; the number in the corner has to say so.
      onPostsWritten()

      // And the offer is answered whether or not there is a sentence to carry on with. A page
      // reloaded onto this offer has no such sentence, and a yes that stayed tappable was a
      // second credit at the second tap.
      setMessages((current) =>
        current.map((entry) => (entry.offer === 'discards' && !entry.answered ? { ...entry, answered: true } : entry)),
      )
    } catch (failure) {
      setMessages((current) => [...current, { from: 'assistant', text: failure.message, error: true }])
      setBusy(false)
      return
    }

    setBusy(false)

    if (lastAsk.current) send(lastAsk.current)
  }, [])

  // The owner tapped a number of pictures. Their words go with it, so the conversation shows the
  // choice as theirs; the server starts the drawing and the wait begins.
  const pickStory = useCallback(
    async (post, choice) => {
      if (!post?.id) return

      const words = t('campaign.storyboardPick', { count: choice.scenes, credits: choice.credits })
      setMessages((current) => [...current, { from: 'owner', text: words }])
      setBusy(true)

      try {
        const result = await api.storyboard(post.id, choice.scenes, words)

        setMessages((current) => [
          ...current,
          { from: 'assistant', text: '', working: result.working, notes: result.notes ?? [] },
        ])

        if (result.working) setWorking(true)
      } catch (failure) {
        setMessages((current) => [...current, { from: 'assistant', text: failure.message, error: true }])
      } finally {
        setBusy(false)
      }
    },
    [t],
  )

  const openPostById = useCallback(async (postId) => {
    try {
      const post = await api.post(postId)
      setOpenPost(post)

      // Opening one is the plainest way of saying which post the next sentence is about.
      onFocus?.({ id: post.id, name: post.products?.[0]?.name ?? null })
    } catch {
      // Deleted since the report was written. The card simply does nothing, like a dead link.
    }
  }, [onFocus])

  // Which layout. The campaign was chosen and written down before the question was asked, so the
  // tap starts exactly the campaign that was offered rather than a second, similar one.
  const pickLayout = useCallback(async (turnId, key, name) => {
    if (!turnId || busy) return

    setMessages((current) => [...current, { from: 'owner', text: name }])
    setBusy(true)

    try {
      const result = await api.chooseLayout(turnId, key, name)

      setMessages((current) => [
        ...current,
        { from: 'assistant', text: '', working: result.working, notes: result.notes ?? [] },
      ])

      if (result.working) setWorking(true)
    } catch (failure) {
      setMessages((current) => [...current, { from: 'assistant', text: failure.message, error: true }])
    } finally {
      setBusy(false)
    }
  }, [busy])

  // "Врати" under a card. The same endpoint the sheet's own undo uses; the answer is written into
  // the conversation, because that is where the change was asked for.
  const undoById = useCallback(async (postId) => {
    if (undoing) return
    setUndoing(postId)

    try {
      await api.undoPost(postId)
      setMessages((current) => [
        ...current,
        { from: 'assistant', text: '', notes: [{ code: 'undone', args: { id: postId } }] },
      ])
      onPostsWritten()
    } catch (failure) {
      setMessages((current) => [...current, { from: 'assistant', text: failure.message, error: true }])
    } finally {
      setUndoing(null)
    }
  }, [onPostsWritten, undoing])

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages])

  // Writing happens in the jobs host, so the queue fills a little after the conversation moves on
  // — and the jobs host says how it went by writing into the conversation. Both are watched here:
  // without the second, a campaign that matched nothing ended on "in about a minute" and silence.
  useEffect(() => {
    if (!working) return undefined

    async function look() {
      if (document.visibilityState !== 'visible') return

      wrote.current()

      try {
        const turns = await api.assistantTurns()
        const report = turns.findLast((turn) => DONE_MARKERS.includes(turn.marker))

        if (report && report.id > reportSeen.current) {
          reportSeen.current = report.id
          setMessages(turns.map(fromStored))
          setWorking(false)
          loadStarters()
        }
      } catch {
        // The next tick tries again; a failed poll is not worth saying anything about.
      }
    }

    const timer = setInterval(look, 6000)
    const stop = setTimeout(() => setWorking(false), 5 * 60 * 1000)

    return () => {
      clearInterval(timer)
      clearTimeout(stop)
    }

    // Only `working`. With onPostsWritten in here the effect was torn down and rebuilt every time
    // the parent re-rendered — which the poll itself causes, every six seconds — so the five-minute
    // give-up was reset before it could ever fire, and a job that died left the spinner turning
    // for as long as the screen stayed open.
  }, [working])

  const send = useCallback(
    async (spoken, marker = null) => {
      const message = (spoken ?? text).trim()
      if (!message || busy) return

      const mine = { from: 'owner', text: message, marker }
      setMessages((current) => [...current, mine])
      if (!marker) lastAsk.current = message
      setText('')
      setOtherAsked(false)

      // The box grew to fit what was typed; emptying it has to shrink it back.
      if (textRef.current) textRef.current.style.height = 'auto'
      setBusy(true)

      try {
        // The history travels no longer: the server keeps the conversation and reads its own.
        // Answered in the language the portal is being read in, not the one the shop posts in.
        // What they have open travels with the line. Without it "цената горе" is answered with a
        // question about which post they mean, asked of somebody who is looking at one.
        const result = await api.assistant(message, marker, i18n.resolvedLanguage, focus?.id ?? null)

        setMessages((current) => [
          ...current,
          {
            from: 'assistant',
            text: result.reply,
            questions: result.questions ?? [],
            written: result.postIds?.length ?? 0,
            action: result.action,
            posts: result.posts ?? [],
            working: result.working,
            truncated: result.catalogTruncated,
            notes: result.notes ?? [],
            offer: result.offer ?? null,
            choices: result.choices ?? null,
            // The live reply has to carry the same things a stored turn does, or the offer shows
            // its three pictures only after a reload — which is how the layout question arrived
            // with one button and no pictures.
            layouts: result.layouts ?? null,
            turnId: result.turnId ?? null,
          },
        ])

        if (result.working) setWorking(true)
        if (result.postIds?.length) onPostsWritten()

        // The starting points are read again only when they are about to be shown. A reply that
        // asks something, or is still writing, is followed by another reply before they are.
        if (!result.working && !result.questions?.length) loadStarters()
      } catch (failure) {
        // The words never arrived. They stay in the transcript, marked, and the question they
        // were answering stays open — so the same chip can be tapped again.
        setMessages((current) => [
          ...current.map((entry) => (entry === mine ? { ...entry, failed: true } : entry)),
          { from: 'assistant', text: failure.message, error: true },
        ])
      } finally {
        setBusy(false)
      }
    },
    [busy, focus, i18n.resolvedLanguage, loadStarters, onPostsWritten, text],
  )

  // What was said before this tab existed.
  useEffect(() => {
    let cancelled = false

    api
      .assistantTurns()
      .then((turns) => {
        if (cancelled) return

        const drawn = turns.map(fromStored)
        setMessages(drawn)

        // Everything already reported is old news; only something newer ends a wait.
        reportSeen.current = turns.reduce(
          (last, turn) => (DONE_MARKERS.includes(turn.marker) ? turn.id : last), 0)

        // A campaign that was still running when this tab was left picks its loader back up.
        if (stillWaiting(drawn)) setWorking(true)
      })
      .catch(() => {
        // An unreadable conversation is not a reason to refuse to hold a new one.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })

    loadStarters()

    return () => {
      cancelled = true
    }
  }, [])

  const said = (marker) => messages.some((entry) => entry.marker === marker)

  // The assistant introduces itself once in the life of the business — not once per tab.
  useEffect(() => {
    if (loaded && !busy && messages.length === 0) send(t('campaign.opening'), OPENING)
  }, [loaded])

  // The website has just been read. The assistant picks the conversation back up on its own rather
  // than leaving the owner to work out that it is now waiting for them — but only the first time,
  // and only after the stored turns say it has not already done so.
  useEffect(() => {
    if (loaded && siteRead && !said(SITE_READ)) send(t('campaign.siteRead'), SITE_READ)
  }, [loaded, siteRead])

  // The two turns nobody types stay hidden; the campaign's own report does not.
  // Hidden: the two turns nobody types, and any turn with nothing in it — an older stored turn
  // whose payload this portal did not understand used to draw as an empty grey bubble.
  const visible = messages.filter(
    (entry) =>
      entry.marker !== OPENING &&
      entry.marker !== SITE_READ &&
      (entry.text ||
        entry.campaign ||
        entry.recast ||
        entry.design ||
        entry.storyboard ||
        entry.offer ||
        entry.questions?.length ||
        entry.notes?.length ||
        entry.working),
  )

  // The one turn whose loader may move. Counted over what is actually drawn, so the dots land on
  // the bubble they belong to.
  const lastWorkingIndex = visible.reduce(
    (last, entry, index) => (entry.working ? index : last), -1)

  // Only the newest turn is still asking. Everything before it was answered, or overtaken by a
  // report, or simply left; its buttons stay in the transcript as a record of what was offered,
  // never as something to tap. A chip that still worked a week later was a second campaign nobody
  // asked for.
  // A report written by the jobs host — a campaign finished, a story drawn, pictures dressed — is
  // news, not a reply. It lands whenever the job is done, which can be while a question is open,
  // and it neither answers that question nor closes it: the options went missing under the
  // owner's thumb the moment an earlier job reported.
  const isReport = (entry) => Boolean(entry.campaign || entry.recast || entry.design || entry.storyboard)

  // Turns that do not move the conversation on: a line that never reached the server, the notice
  // that says so, and a report. None of them closes the question before it.
  const aside = (entry) => Boolean(entry.error || entry.failed || isReport(entry))

  // The newest turn that was actually said.
  const lastIndex = visible.reduce((last, entry, index) => (aside(entry) ? last : index), -1)

  // What the owner said straight after a turn that asked something: the answer to mark. Only the
  // next thing said counts — a sentence three turns later is about something else.
  const answerAfter = (index) => {
    const next = visible.slice(index + 1).find((entry) => !aside(entry))
    return next?.from === 'owner' ? (next.text ?? null) : null
  }

  // Whether the newest turn is waiting on the owner: an open question, or an offer that is
  // answered here, in the conversation. The pictures offer is answered in the profile and the
  // credits offer at the till, and an offer that kept the starting points away for good hid them
  // from the one person who needed them — the owner coming back with the pictures uploaded.
  const lastEntry = visible[lastIndex]
  const askingNow =
    lastEntry?.from === 'assistant' &&
    (lastEntry.questions?.length > 0 ||
      lastEntry.offer === 'layout' ||
      lastEntry.offer === 'storyboard' ||
      (lastEntry.offer === 'discards' && !lastEntry.answered) ||
      (lastEntry.offer === 'pictures' && !(starters?.contentSets > 0)))

  // The cursor into the box, on "Друго…": the offered answers were not the answer.
  const askOther = () => {
    setOtherAsked(true)
    textRef.current?.focus()
  }

  return (
    <section className="card card--chat">
      <div className="chat">
        {visible.map((entry, index) => (
            <div
              key={index}
              className={`bubble bubble--${entry.from}${entry.error ? ' bubble--error' : ''}${entry.failed ? ' bubble--failed' : ''}`}
            >
              {entry.campaign ? (
                <CampaignReport
                  report={entry.campaign}
                  onOpenPost={openPostById}
                  onShowCalendar={onShowCalendar}
                  onShowProfile={onShowProfile}
                />
              ) : entry.design ? (
                <DesignReport design={entry.design} reply={entry.text} />
              ) : entry.recast ? (
                <RecastReport recast={entry.recast} onOpenPost={openPostById} />
              ) : entry.storyboard ? (
                <StoryboardReport story={entry.storyboard} onOpenPost={openPostById} />
              ) : entry.offer === 'storyboard' ? (
                <>
                  {/* Why there is no video, then the way on. */}
                  {entry.notes?.length > 0 && <div className="bubble__note">{noteText(t, entry.notes[0])}</div>}
                  <StoryboardOffer
                    /* Disabled while a story is being drawn, not only while the tap is in flight:
                       three taps used to be three stories. */
                    busy={busy || working}
                    settled={index !== lastIndex}
                    chosen={answerAfter(index)}
                    post={entry.posts?.map(normalisePost)[0]}
                    choices={entry.choices}
                    onPick={(choice) => pickStory(entry.posts?.map(normalisePost)[0], choice)}
                    onDecline={() => send(t('common.no'))}
                  />
                </>
              ) : entry.offer === 'layout' ? (
                <LayoutOffer
                  busy={busy || working}
                  settled={index !== lastIndex}
                  chosen={answerAfter(index)}
                  layouts={entry.layouts}
                  onPick={(key, name) => pickLayout(entry.turnId, key, name)}
                />
              ) : entry.offer === 'pictures' ? (
                <div className="offer">
                  <p>{t('campaign.picturesOffer')}</p>
                  {index === lastIndex && (
                    <button type="button" className="button" onClick={onShowProfile}>
                      {t('campaign.picturesOpen')}
                    </button>
                  )}
                </div>
              ) : entry.offer === 'discards' ? (
                <DiscardsOffer
                  busy={busy}
                  settled={index !== lastIndex || Boolean(entry.answered)}
                  chosen={entry.answered ? t('campaign.discardsAccept') : answerAfter(index)}
                  onAccept={acceptOffer}
                  onDecline={() => send(t('common.no'))}
                />
              ) : entry.offer === 'credits' || entry.offer === 'room' ? (
                <CreditsOffer
                  busy={busy}
                  settled={index !== lastIndex}
                  onAsked={onPostsWritten}
                  /* Out of credits is one sentence; out of room is arithmetic, and the note
                     carries the two numbers it is made of. */
                  lead={entry.notes?.length > 0 ? noteText(t, entry.notes[0]) : t('campaign.creditsOffer')}
                  ways={entry.offer === 'room'}
                />
              ) : (
                entry.text || (entry.notes?.length > 0 && !entry.campaign ? noteText(t, entry.notes[0]) : null)
              )}

              {entry.questions?.map((question) => (
                <div key={question.text} className="question">
                  <div className="question__text">{question.text}</div>

                  {/* Open on the newest turn only; answered turns keep the answer, marked. */}
                  {index === lastIndex ? (
                    <QuestionOptions
                      question={question}
                      disabled={busy || working}
                      onPick={(option) => send(option)}
                      onOther={askOther}
                    />
                  ) : (
                    <Chosen answer={answerAfter(index)} among={question.options} />
                  )}
                </div>
              ))}

              {entry.working && (
                <div className="bubble__note">
                  {/* What is actually happening. "Ги пишувам сега" under a design change was a
                      small lie every time: nothing is written when pictures are dressed again. */}
                  {t(
                    entry.action === 'Design' || entry.action === 'ChangeFormat' || entry.action === 'ChoosePhotos'
                      ? 'campaign.dressing'
                      : entry.action === 'Redraw' || entry.action === 'Storyboard'
                        ? 'campaign.drawing'
                        : 'campaign.working',
                  )}
                  {/* Alive only while the wait is: a turn stored last week also says "working",
                      but its report already follows it in the transcript. */}
                  {working && index === lastWorkingIndex && (
                    <span className="writing" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  )}
                </div>
              )}

              {entry.written > 0 && (
                <div className="bubble__note">
                  {entry.action === 'Regenerate'
                    ? t('campaign.regenerated')
                    : entry.action && entry.action !== 'CreatePosts'
                      ? t('campaign.changed', { count: entry.written })
                      : t('campaign.written', { count: entry.written })}
                </div>
              )}

              {/* And the posts themselves, tappable, right where they were announced. */}
              {entry.posts?.length > 0 && (
                <PostCards posts={entry.posts} onOpenPost={openPostById} onUndo={undoById} />
              )}

              {entry.truncated && <div className="bubble__note">{t('campaign.truncated')}</div>}

              {/* Under a reply, the notes are the small print: what could not be done and why. A
                  refusal with no reply was already shown above as the bubble itself. */}
              {entry.notes?.length > 0 && entry.text && (
                <div className={`bubble__note${entry.notes.some((note) => isBadNote(note)) ? ' bubble__note--bad' : ''}`}>
                  {entry.notes.map((note) => noteText(t, note)).filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          ))}

        {busy && <div className="bubble bubble--assistant">{t('campaign.thinking')}</div>}
        <div ref={endRef} />
      </div>

      {/* Starting points, when nothing is being asked, nothing is in flight and no post is on the
          table. An open question and a row of starting points at once would be two conversations,
          and under a post being worked on the row of things to start was three rows of noise
          between the owner and the post. */}
      {loaded && !busy && !working && !askingNow && !focus && (
        <Starters state={starters} disabled={busy} onSay={(sentence) => send(sentence)} onShowProfile={onShowProfile} />
      )}

      {focus && (
        <div className="focuschip">
          <span className="focuschip__label">
            {t('campaign.workingOn', { name: focus.name ?? `#${focus.id}` })}
          </span>
          <button
            type="button"
            className="focuschip__off"
            aria-label={t('campaign.workingOnOff')}
            title={t('campaign.workingOnOff')}
            onClick={() => onFocus?.(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* The sentences said most often about the post on the table, under a thumb. */}
      {focus && !askingNow && <QuickActions disabled={busy || working} onSay={(sentence) => send(sentence)} />}

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault()
          send()
        }}
      >
        {/* A textarea rather than a single line: people write two sentences about their shop and
            the second one used to scroll out of sight while they typed it. It grows to a few lines
            and then scrolls. */}
        <textarea
          ref={textRef}
          className="composer__text"
          rows={1}
          value={text}
          placeholder={t(otherAsked ? 'campaign.otherPlaceholder' : 'campaign.placeholder')}
          disabled={busy}
          onChange={(event) => setText(event.target.value)}
          onInput={(event) => {
            event.target.style.height = 'auto'
            event.target.style.height = `${Math.min(event.target.scrollHeight, 160)}px`
          }}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter starts a line. On a phone the key is "go" and there is no
            // shift to hold, so this is the desk behaviour and the thumb one at the same time.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
        />

        <button
          type="submit"
          className="composer__send"
          disabled={busy || !text.trim()}
          aria-busy={busy || undefined}
          aria-label={t('campaign.send')}
          title={t('campaign.send')}
        >
          {/* The arrow becomes a ring while the assistant is thinking. Same button, same place —
              the answer is what changes, not the furniture. */}
          {busy ? <span className="spinner" aria-hidden="true" /> : <SendIcon />}
        </button>
      </form>

      <PostModal
        post={openPost}
        business={business}
        timezoneId={timezoneId}
        canManage={canManage}
        busy={busy}
        onClose={() => setOpenPost(null)}
        onChanged={() => {
          openPostById(openPost.id)
          onPostsWritten()
        }}
        onReplaced={(id) => openPostById(id)}
        onAskDesigner={(post) => onFocus?.({ id: post.id, name: post.products?.[0]?.name ?? null })}
      />
    </section>
  )
}
